import React from 'react';
import {
  Box,
  Grid,
  Card,
  Stack,
  Divider,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Link,
} from '@mui/material';

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { FaCircle, FaUsers } from 'react-icons/fa';
import { IoIosArrowRoundForward } from 'react-icons/io';
import { IoWallet } from 'react-icons/io5';
import { MdDiscount } from 'react-icons/md';

const currencyEGP = (n) => `${Number(n).toLocaleString()} EGP`;

const insightIconMap = {
  budget: <IoWallet size={18} color='#3FBEAC' />,
  users: <FaUsers size={18} color='#7E1BAD' />,
  discount: <MdDiscount size={18} color='#3E9FF6' />,
};

const SectionNumber = ({ n }) => (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: '50%',
      border: `1.5px solid #3FBEAC`,
      color: "#3FBEAC",
      bgcolor: "#ECF9F7",
      fontSize: 12,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {n}
  </Box>
);

const Field = ({ label, value, valueColor }) => (
  <Box>
    <Typography variant="caption" color="#535353" sx={{ display: 'block', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ color: valueColor }}>
      {value}
    </Typography>
  </Box>
);

const InsightRow = ({ icon, text }) => (
  <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 2.53 }}>
    <Box sx={{
      width: 22,
      height: 22,
      borderRadius: '50%',
      border: `1.5px solid #E8E8E8`,
      color: "#3FBEAC",
      fontSize: 12,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      p: .5
    }}>{icon}</Box>
    <Typography variant="body2">{text}</Typography>
  </Stack>
);

const TrendChart = ({ data }) => (
  <ResponsiveContainer width="100%" height={270}>
    <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
      <defs>
        <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor={"#34B3F1"} stopOpacity={0.15} />
          <stop offset="95%" stopColor={"#3FBEAC"} stopOpacity={0} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={"#E8E8E8"} />
      <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#8A94A6' }} />
      <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#8A94A6' }} />
      <Tooltip
        formatter={(value) => [value, 'Cashback Issued']}
        contentStyle={{ borderRadius: 8, border: `1px solid #E8E8E8`, fontSize: 12 }}
      />
      <Area type="monotone" dataKey="redemptions" stroke={"#3FBEAC"} strokeWidth={4} fill="url(#trendFill)" />
    </AreaChart>
  </ResponsiveContainer>
);

const OverviewCashBackTab = ({ data }) => {
  const { basicInfo, validity, fundingLiability, eligibilityRulesOverview, quickInsights, cashbackIssuanceTrend } = data;

  return (
    <Grid container spacing={2.5} alignItems="stretch">
      <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#E8E8E8", p: 1.5, width: '100%', height: '100%' }}>
          <Box my={1} borderBottom={"1px solid #E8E8E8"} py={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" gap={1.5} alignItems="center">
                <SectionNumber n={1} />
                <Typography fontWeight={600}>Basic Information</Typography>
              </Box>
            </Box>
            <Box sx={{ p: { xs: 1, sm: 2 } }}>
              <Grid container spacing={3}>
                <Grid item xs={6} sm={3}>
                  <Field label="Trigger Type" value={basicInfo.triggerType} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Field label="Cashback Value" value={`${basicInfo.cashbackValue}%`} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Field label="Max Cap per Order" value={currencyEGP(basicInfo.maxCapPerOrder)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Field label="Min Order Spend" value={currencyEGP(basicInfo.minOrderSpend)} />
                </Grid>
                <Grid item xs={6} sm={3} sx={{ mt: 2.5 }}>
                  <Field label="Validity Period" value={`${basicInfo.validityPeriod} Days`} />
                </Grid>
                <Grid item xs={6} sm={3} sx={{ mt: 2.5 }}>
                  <Field label="Max Uses per User" value={basicInfo.maxUsesPerUser} />
                </Grid>
                <Grid item xs={6} sm={3} sx={{ mt: 2.5 }}>
                  <Field label="Total Claims Limit" value={basicInfo.totalClaimsLimit.toLocaleString()} />
                </Grid>
                <Grid item xs={6} sm={3} sx={{ mt: 2.5 }}>
                  <Field
                    label="Remaining Claims"
                    value={`${basicInfo.remainingClaims.toLocaleString()} (${basicInfo.remainingClaimsPercent}%)`}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2.5 }}>
                <Typography variant="caption" color="#535353" sx={{ display: 'block', mb: 0.5 }}>
                  Status
                </Typography>
                <Box display={"flex"} alignItems={"center"} gap={.5}>
                  <FaCircle size={10} color='#3FBEAC' />
                  <Typography variant="body2" fontWeight={600}>{basicInfo.status}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box my={1} borderBottom={"1px solid #E8E8E8"} py={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ px: 1, py: .5, my: 1, justifyContent: { xs: "normal", md: "space-between" }, flexDirection: { xs: "column", md: "row" }, gap: { xs: 2, md: 0 }, alignItems: { xs: "flex-start", md: "center" } }}>
              <Box display="flex" gap={1.5} alignItems="center">
                <SectionNumber n={2} />
                <Typography fontWeight={600}>Cashback Issuance Trend</Typography>
              </Box>
              <Box>
                <FormControl size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: 13 } }}>
                  <Select defaultValue="all" IconComponent={KeyboardArrowDownIcon}>
                    <MenuItem value="all">Last 30 Days</MenuItem>
                    <MenuItem value="active">Last 20 Days</MenuItem>
                    <MenuItem value="inactive">Last 10 Days</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <TrendChart data={cashbackIssuanceTrend} />
          </Box>

          <Box my={1} py={2}>
            <Box sx={{ px: 1, py: .5, my: .5 }}>
              <Box display="flex" gap={1.5} alignItems="center">
                <SectionNumber n={3} />
                <Typography fontWeight={600}>Eligibility & Rules</Typography>
              </Box>
              <Box pt={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Field label="Eligible Customers" value={eligibilityRulesOverview.eligibleCustomers} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Field label="Customer Tiers" value={eligibilityRulesOverview.customerTiers} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Field label="Eligible Services" value={eligibilityRulesOverview.eligibleServices} />
                  </Grid>
                  <Grid item xs={12} sm={3} sx={{ mt: 2 }}>
                    <Field label="Excluded Items" value={eligibilityRulesOverview.excludedItems} />
                  </Grid>
                  <Grid item xs={12} sm={3} sx={{ mt: 2 }}>
                    <Field label="Stacking" value={eligibilityRulesOverview.stacking} />
                  </Grid>
                  <Grid item xs={12} sm={3} sx={{ mt: 2 }}>
                    <Field label="Wallet Validity" value={`${eligibilityRulesOverview.walletValidity} Days`} />
                  </Grid>
                  <Grid item xs={12} sm={3} sx={{ mt: 2 }}>
                    <Field label="Eligible Payment Methods" value={eligibilityRulesOverview.eligiblePaymentMethods} />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>
        </Card>
      </Grid>

      <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#E8E8E8", p: 1.5 }}>
            <Box sx={{ p: 1, my: .5, borderBottom: "1px solid #E8E8E8" }}>
              <Typography fontWeight={600}>Validity</Typography>
            </Box>
            <Stack divider={<Divider />} spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
                <Typography variant='body2'>Start Date</Typography>
                <Typography variant='body2' fontWeight={600}>{validity.startDate}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
                <Typography variant='body2'>End Date</Typography>
                <Typography variant='body2' fontWeight={600}>{validity.endDate}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
                <Typography variant='body2'>Days</Typography>
                <Typography variant='body2' fontWeight={600}>{`${validity.days} Days`}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
                <Typography variant='body2'>Time Restriction</Typography>
                <Typography variant='body2' fontWeight={600}>{validity.timeRestriction}</Typography>
              </Stack>
            </Stack>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#E8E8E8", p: 1.5, my: 2 }}>
            <Box sx={{ p: 1, my: .5, borderBottom: "1px solid #E8E8E8" }}>
              <Typography fontWeight={600}>Funding & Liability</Typography>
            </Box>
            <Box>
              <Box display={"flex"} justifyContent={"space-between"} py={1.53}>
                <Typography variant='body2'>Funding Source</Typography>
                <Typography variant='body2' fontWeight={600}>{fundingLiability.fundingSource}</Typography>
              </Box>
              <Box display={"flex"} justifyContent={"space-between"} py={1.53}>
                <Typography variant='body2'>ESTBN Share</Typography>
                <Typography variant='body2' fontWeight={600}>{`${fundingLiability.estbnShare}%`}</Typography>
              </Box>
              <Box display={"flex"} justifyContent={"space-between"} py={1.53} borderBottom={"1px solid #D9D9D9"}>
                <Typography variant='body2'>Provider Share</Typography>
                <Typography variant='body2' fontWeight={600}>{`${fundingLiability.providerShare}%`}</Typography>
              </Box>
              <Box display={"flex"} justifyContent={"space-between"} py={1.53}>
                <Typography variant='body2'>Total Committed Budget</Typography>
                <Typography variant='body2' fontWeight={600}>{currencyEGP(fundingLiability.totalCommittedBudget)}</Typography>
              </Box>
              <Box display={"flex"} justifyContent={"space-between"} py={1.53}>
                <Typography variant='body2'>Total Redeemed Cost</Typography>
                <Typography variant='body2' fontWeight={600}>{currencyEGP(fundingLiability.totalRedeemedCost)}</Typography>
              </Box>
              <Box display={"flex"} justifyContent={"space-between"} py={1.53}>
                <Typography variant='body2'>Remaining Budget</Typography>
                <Typography variant='body2' fontWeight={600} color='#159D89'>
                  {`${currencyEGP(fundingLiability.remainingBudget)} (${fundingLiability.remainingBudgetPercent}%)`}
                </Typography>
              </Box>
            </Box>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#E8E8E8", p: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" borderBottom={"1px solid #E8E8E8"} py={1} my={.5}>
              <Typography fontWeight={600}>Quick Insights</Typography>
              <Link sx={{ color: "#159D89", textDecorationLine: "none", fontSize: 13, display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}>
                View all <IoIosArrowRoundForward size={18} />
              </Link>
            </Box>
            <Stack divider={<Divider />} sx={{ flexGrow: 1 }}>
              {quickInsights.map((insight, i) => (
                <InsightRow key={i} icon={insightIconMap[insight.type]} text={insight.text} />
              ))}
            </Stack>
          </Card>
        </Box>
      </Grid>
    </Grid>
  );
};

export default OverviewCashBackTab;